import { makeRequest } from '@/network/api';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useAccount, useDisconnect } from 'wagmi';

import { AuthContext } from '@/hooks/useAuth';
import { useCompliance } from '@/hooks/useCompliance';
import { useUpdateEffect } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Address } from 'viem';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const currentPath = useLocation().pathname;
  const { address } = useAccount();
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const queryClient = useQueryClient();
  const { disconnect } = useDisconnect();
  const [role, setRole] = useState<string | undefined>(undefined);

  const { data: session, isLoading } = useQuery({
    queryKey: ['session', address],
    queryFn: async () => {
      const res = await makeRequest('/auth/checkSession').then((res) => res.json());
      if (res.error) {
        throw new Error(res.error);
      }
      return res;
    },
    enabled: !!address,
  });

  const [searchParams] = useSearchParams();

  // ✅ Capture query param on first render
  const [referrerFromUrl, setReferrerFromUrl] = useState(() => searchParams.get('referrer'));

  useEffect(() => {
    if (!referrerFromUrl) {
      setReferrerFromUrl(searchParams.get('referrer')); // Set only if not already stored
    }
  }, [searchParams, referrerFromUrl]);

  useEffect(() => {
    if (session) {
      setAuthStateFromLoginResponse(session);
    }
  }, [session]);

  const isAuthenticated = !!session;

  const authedViaLogin = !!session;

  const logout = useCallback(async () => {
    await makeRequest('/auth/logout', { method: 'POST' });
    queryClient.invalidateQueries();
    disconnect();
  }, [queryClient, disconnect]);

  const reconnect = useCallback(async () => {
    await makeRequest('/auth/checkSession').then((res) => res.json());
  }, []);

  const { geoCompliance } = useCompliance();

  const setAuthStateFromLoginResponse = useCallback(
    async (resJson: { error: string; hasCompletedOnboarding: boolean; role?: string }) => {
      try {
        if (resJson.error) {
          setHasCompletedOnboarding(false);
          setRole(undefined);
          throw new Error(resJson?.error ?? 'Error with login response');
        }
        setHasCompletedOnboarding(resJson.hasCompletedOnboarding);
        setRole(resJson.role);
        if (!resJson.hasCompletedOnboarding && !currentPath.startsWith('/onboarding')) {
          navigate('/onboarding/welcome');
        }

        return !resJson.error;
      } catch (err) {
        console.error('Error setting auth state from login response', err);
        return false;
      }
    },
    [currentPath, navigate],
  );

  useUpdateEffect(() => {
    if (geoCompliance?.restrictAccess) {
      logout();
    }
  }, [geoCompliance?.restrictAccess, isAuthenticated]);

  return (
    <AuthContext.Provider
      value={{
        address: address as Address,
        isAuthenticated,
        isLoading,
        authedViaLogin,
        logout: logout,
        hasCompletedOnboarding,
        reconnect,
        referrerFromUrl,
        role,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
